import { useContext, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { z } from 'zod';
import {
  addUser,
  deleteUser as deleteUserNetwork,
  getUsers,
  resendActivationEmail,
  setUserRole,
  updateUser as updateUserNetwork,
} from '../api/Network';
import LoadingPage from '../components/LoadingPage';
import AddUserModal from '../components/Modals/AddUserModal';
import EditUserModal from '../components/Modals/EditUserModal';
import { ConfirmationModalContext } from '../contexts/ConfirmationProvider';
import { CreateUser } from '../types/CreateUser';
import {
  ADMIN_ROLE_NAME,
  DEVELOPER_ROLE_NAME,
  getRoleIdByName,
  REPORTING_ONLY_ROLE_NAME,
  RETURN_MAIL_TEAM_ROLE_NAME,
} from '../types/Role';
import { UpdateUser } from '../types/UpdateUser';
import { User } from '../types/User';

const defaultUser: User = {
  id: 0,
  firstName: '',
  lastName: '',
  username: '',
  email: '',
  role: REPORTING_ONLY_ROLE_NAME,
  oktaStatus: '',
};
//zod-forms
export const userSchema = z.object({
  firstName: z.string().min(2, 'First name is required'),
  lastName: z.string().min(2, 'Last name is required'),
  email: z.email('Invalid email'),
  role: z
    .string()
    .refine(
      (val) => val === REPORTING_ONLY_ROLE_NAME || val === RETURN_MAIL_TEAM_ROLE_NAME || val === ADMIN_ROLE_NAME,
      {
        message: 'Role must be reporting only, return mail team, or admin',
      },
    ),
});
export type UserFormData = z.infer<typeof userSchema>;

export const UserManagement = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery(['users'], () => getUsers(), {
    onError: (error: Error) => toast.error(error.message),
  });

  const [selectedUser, setSelectedUser] = useState<User>(defaultUser);
  const [newUser, setNewUser] = useState<User>(defaultUser);
  const { showModal: showConfirmationModal } = useContext(ConfirmationModalContext);

  const resendActivation = async (userId: number) => {
    await resendActivationEmail(userId);
  };

  const resendActivationConfirmation = (user: User) => {
    showConfirmationModal({
      title: 'Resend Activation Email',
      message: `This will resend an activation email from Okta to ${user.username} to invite them to set up their account.`,
      show: true,
      okBtnText: 'Send Email',
      cancelBtnText: 'Cancel',
      action: async () => await resendActivation(user.id),
    });
  };
  //Opening Modals
  const openEditModal = (user: User) => {
    setSelectedUser(user);
    (document.getElementById('edit_modal') as HTMLDialogElement).showModal();
  };
  const openAddModal = () => {
    (document.getElementById('add_modal') as HTMLDialogElement).showModal();
    setNewUser(defaultUser);
  };

  const handleDelete = (user: User) => {
    setSelectedUser(user);
    (document.getElementById('delete_modal') as HTMLDialogElement).showModal();
  };
  const { mutate: deleteUser, isLoading: isDeleteLoading } = useMutation(
    (userId: number) => {
      return deleteUserNetwork(userId);
    },
    {
      onSuccess: async (response) => {
        await queryClient.invalidateQueries({ queryKey: 'users' });
        toast.success('Successfuly deleted user');
        (document.getElementById('delete_modal') as HTMLDialogElement).close();
      },
      onError: async (error: Error) => {
        toast.error(error.message);
      },
    },
  );

  const { mutate: handleAddUser, isLoading: isCreatingLoading } = useMutation(
    async (data: UserFormData) => {
      const newUser: CreateUser = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        username: data.email,
      };
      const { data: user } = await addUser(newUser);

      if (user && user?.role !== data.role) {
        const roleId = getRoleIdByName(data.role);

        if (roleId) {
          await setUserRole(user?.id, roleId);
        }
      }
    },
    {
      onSuccess: async (response) => {
        await queryClient.invalidateQueries({ queryKey: 'users' });
        toast.success('Successfully created user');
        (document.getElementById('add_modal') as HTMLDialogElement).close();
      },
      onError: async (error: Error) => {
        toast.error(error.message);
      },
    },
  );
  const { mutate: editUser, isLoading: isEditLoading } = useMutation(
    async (data: UserFormData) => {
      if (
        selectedUser &&
        (selectedUser?.firstName !== data.firstName ||
          selectedUser?.lastName !== data.lastName ||
          selectedUser?.email !== data.email)
      ) {
        const updateUser: UpdateUser = {
          email: selectedUser?.email !== data.email ? data.email : null,
          firstName: selectedUser?.firstName !== data.firstName ? data.firstName : null,
          lastName: selectedUser?.lastName !== data.lastName ? data.lastName : null,
        };

        await updateUserNetwork(selectedUser.id, updateUser);
      }

      if (selectedUser && selectedUser?.role !== data.role) {
        const roleId = getRoleIdByName(data.role);

        if (roleId) {
          await setUserRole(selectedUser?.id, roleId);
        }
      }
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: 'users' });
        setSelectedUser(defaultUser);
        toast.success('Successfully edited user');
        (document.getElementById('edit_modal') as HTMLDialogElement).close();
      },
      onError: (error: Error) => {
        toast.error(error.message);
      },
    },
  );
  console.log(data?.data);
  if (isLoading || data?.data == null) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingPage />
      </div>
    );
  }
  
  return (
    <div className="p-2 bg-base-200 rounded-box h-full min-h-0 flex flex-col">
      <div className="flex flex-row justify-between">
        <h2 className="text-3xl font-bold text-primary mb-4">User Management</h2>
        <button className="btn btn-success btn-xs" onClick={openAddModal}>
          Create User
        </button>
      </div>
      <div className="divider divider-primary m-0" />
     <div className="overflow-y-auto flex-1 min-h-0">
        <table className="table table-pin-cols ">
          <thead className="sticky top-0 bg-base-300 text-primary">
            <tr className="bg-base-300">
            <th>Name</th>
            <th>Username</th>
            <th>Email</th>
            <th>Last Active</th>
            <th>Role</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.data.map((user) => (
            <tr key={user.id}>
              <td className="font-semibold">
                {user.firstName} {user.lastName}
              </td>
              <td>{user.username}</td>
              <td>{user.email}</td>
              <td>{user.lastActiveDate != null ? new Date(user.lastActiveDate).toLocaleDateString('en-US') : null}</td>
              <td>
                <span
                  className={`badge ${
                    user.role === ADMIN_ROLE_NAME
                      ? 'badge-error'
                      : user.role === RETURN_MAIL_TEAM_ROLE_NAME
                        ? 'badge-warning'
                        : user.role === REPORTING_ONLY_ROLE_NAME
                          ? 'badge-info'
                          : user.role === DEVELOPER_ROLE_NAME
                            ? 'badge-success'
                            : 'badge-neutral'
                  }`}>
                  {user.role}
                </span>
              </td>
              <td>
                {user.oktaStatus === 'PROVISIONED' ? (
                  <button
                    className="btn btn-sm btn-outline btn-primary mr-2"
                    onClick={() => resendActivationConfirmation(user)}>
                    Resend Activation
                  </button>
                ) : null}
                <button className="btn btn-sm btn-outline btn-info mr-2" onClick={() => openEditModal(user)}>
                  Edit
                </button>
                <button className="btn btn-sm btn-outline btn-error" onClick={() => handleDelete(user)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

      <dialog id="delete_modal" className="modal">
        <div className="modal-box">
          <h3 className="font-bold text-lg text-error">Confirm Deletion</h3>
          <p className="py-4">
            Are you sure you want to delete <span className="font-semibold">{selectedUser?.username}</span>?
          </p>
          <div className="modal-action">
            <button
              className="btn btn-success"
              onClick={() => selectedUser && deleteUser(selectedUser.id)}
              disabled={isDeleteLoading}>
              <div className={`${isDeleteLoading ? 'loading loading-spinner' : ''}`}>Delete</div>
            </button>
            <form method="dialog">
              <button className="btn btn-error">Cancel</button>
            </form>
          </div>
        </div>
      </dialog>
      <AddUserModal onAdd={handleAddUser} isCreatingLoading={isCreatingLoading} />
      <EditUserModal user={selectedUser} onSave={editUser} isEditLoading={isEditLoading} />
    </div>
  );
};
